import { memo, useState } from 'react'

import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { PickerDialogContent } from '@shared/components/PickerDialog'
import { environmentQueryKeys, organizationQueryKeys, workspaceQueryKeys } from 'services/queryKeys'
import { extractRamUserId, resolveDisplayName } from 'utils/userInfo'

type Candidate = {
  /** Bare platform user id — that is what an on-call list stores. */
  userId: string
  name: string | null
  email?: string | null
  /** Where the access comes from. */
  scope: 'workspace' | 'environment' | 'organization'
}

export type PersonPickerDialogContentProps = {
  workspaceId: string
  environmentId: string
  /** Platform user ids already on call — shown as selected and not selectable again. */
  selectedUserIds: string[]
  onSelect: (userId: string, name: string | null) => void
}

/**
 * The platform rejects an on-call user who cannot read this workspace's events, and that right is
 * granted at three levels: on the workspace, inherited from the environment, or inherited from the
 * organization. No endpoint enumerates the resolved set, so the three member lists are merged here
 * to approximate it. Access held through a team is still missing — RAM records the team as the
 * member, not the people in it — so a rejected save is the backstop, not this list.
 */
const PersonPickerDialogContentRaw = ({
  workspaceId,
  environmentId,
  selectedUserIds,
  onSelect,
}: PersonPickerDialogContentProps) => {
  const { t } = useTranslation(['common', 'domain'])

  const [queryValue, setQueryValue] = useState<string>()

  const environmentQuery = useQuery(environmentQueryKeys.detail({ environmentId }))
  const organizationId = environmentQuery.data?.organizationId

  const workspaceMembersQuery = useInfiniteQuery(
    workspaceQueryKeys.memberList({ workspaceId, memberType: 'User', query: queryValue, limit: 50 }),
  )
  const environmentMembersQuery = useInfiniteQuery(
    environmentQueryKeys.memberList({ environmentId, memberType: 'User', query: queryValue, limit: 50 }),
  )
  const organizationMembersQuery = useInfiniteQuery({
    ...organizationQueryKeys.memberList({
      organizationId: organizationId as string,
      memberType: 'User',
      query: queryValue,
      limit: 50,
    }),
    enabled: !!organizationId,
  })

  const queries = [workspaceMembersQuery, environmentMembersQuery, organizationMembersQuery]

  /** The three member models differ only in their role enum; this is all the picker reads. */
  type RamUserMember = {
    id: string
    userInfo?: {
      name?: string | null
      givenName?: string | null
      familyName?: string | null
      email?: string | null
    }
  }

  // Narrowest scope first, so whoever is a direct workspace member is labelled as one.
  const sources: { members: RamUserMember[]; scope: Candidate['scope'] }[] = [
    { members: workspaceMembersQuery.data?.pages.flatMap((page) => page.items) ?? [], scope: 'workspace' },
    { members: environmentMembersQuery.data?.pages.flatMap((page) => page.items) ?? [], scope: 'environment' },
    { members: organizationMembersQuery.data?.pages.flatMap((page) => page.items) ?? [], scope: 'organization' },
  ]

  const candidates: Candidate[] = []
  const seen = new Set<string>()
  sources.forEach(({ members, scope }) =>
    members.forEach((member) => {
      const userId = extractRamUserId(member.id)
      if (seen.has(userId)) return

      seen.add(userId)
      candidates.push({
        userId,
        name: resolveDisplayName(member.userInfo),
        email: member.userInfo?.email,
        scope,
      })
    }),
  )

  const listQuery = {
    isLoading: queries.some((query) => query.isLoading) || environmentQuery.isLoading,
    hasNextPage: queries.some((query) => query.hasNextPage),
    isFetchingNextPage: queries.some((query) => query.isFetchingNextPage),
    fetchNextPage: () =>
      Promise.all(
        queries.filter((query) => query.hasNextPage && !query.isFetchingNextPage).map((query) => query.fetchNextPage()),
      ),
  }

  const scopeLabels = {
    workspace: t('domain:Workspace.Event.OnCall.scopeWorkspace'),
    environment: t('domain:Workspace.Event.OnCall.scopeEnvironment'),
    organization: t('domain:Workspace.Event.OnCall.scopeOrganization'),
  }

  return (
    <PickerDialogContent<Candidate>
      items={candidates}
      listQuery={listQuery}
      onQueryChange={setQueryValue}
      searchPlaceholder={t('domain:Workspace.Event.OnCall.searchPeople')}
      emptyText={t('domain:Workspace.Event.OnCall.noPeople')}
      getKey={(candidate) => candidate.userId}
      getPrimary={(candidate) => candidate.name || candidate.userId}
      getSecondary={(candidate) => [candidate.email, scopeLabels[candidate.scope]].filter(Boolean).join(' · ')}
      isSelected={(candidate) => selectedUserIds.includes(candidate.userId)}
      isDisabled={(candidate) => selectedUserIds.includes(candidate.userId)}
      onSelect={(candidate) => onSelect(candidate.userId, candidate.name)}
    />
  )
}

export const PersonPickerDialogContent = memo(PersonPickerDialogContentRaw)
